/**
 * Planning Phase 3 P3-5 (plan P3-5b): the intake officer tools on the case page (accept with exceptions, the register
 * rows and their class correction, the constraint check and its determinations), Settings' constraint layers, the
 * wording of the tools' refusals and the names of their journal types. Spread into en.ts.
 */

import rulesStrings from './rules-en'

export default {
  intake: {
    rules: rulesStrings.rules,
    title: 'Drawing check tools',
    applicantTitle: 'Drawing check',
    accept: {
      button: 'Accept with exceptions',
      title: 'Accept the failed check with exceptions',
      intro:
        'The check failed only on findings an officer may accept. Accepting stages the register rows of this revision and builds a corrected copy of its drawing data.',
      findings: 'Findings accepted',
      noFindings: 'No acceptable finding is listed. Reload the page before accepting.',
      reason: 'Reason',
      reasonHint:
        'Kept for officers. The applicant sees only that the check was accepted, never this reason or the findings.',
      reasonRequired: 'Give a reason.',
      confirm: 'Accept',
      cancel: 'Cancel',
      done: 'The check was accepted with exceptions.',
    },
    acceptances: {
      title: 'Accepted checks',
      item: 'Revision {{revision}}, accepted {{date}}',
      exceptions: 'Accepted findings: {{codes}}',
      applicant: 'The council accepted the drawing check of revision {{revision}} on {{date}}.',
    },
    corrections: {
      title: 'Class corrections',
      item: '{{row}}: from {{from}} to {{to}}',
      when: 'Revision {{revision}}, corrected {{date}}',
    },
    rows: {
      title: 'Register rows',
      intro: 'The site and plots of this revision as the register holds them.',
      site: 'Site',
      plot: 'Plot {{number}}',
      plotRow: 'Plot (register row {{id}})',
      noClass: 'No class',
      status: {
        candidate: 'Candidate (not staged)',
        staging: 'Staging (awaiting the decision)',
        approved: 'Approved',
        rejected: 'Rejected',
        superseded: 'Superseded',
        withdrawn: 'Withdrawn',
      },
      source: {
        drawing: 'From the drawing',
        officer: 'Corrected by an officer',
      },
      showOnMap: 'Show on map',
      correct: 'Correct class',
      olderRevision: 'Classes are corrected on the current revision only.',
    },
    correct: {
      title: 'Correct the class of {{row}}',
      current: 'Current class: {{class}}',
      class: 'New class',
      choose: 'Choose a class…',
      classRequired: 'Choose a class.',
      unchanged: 'Choose a class different from the current one.',
      reason: 'Reason',
      reasonHint: 'Kept for officers; the applicant does not see it.',
      reasonRequired: 'Give a reason.',
      confirm: 'Save the class',
      cancel: 'Cancel',
      done: 'The class was corrected.',
    },
    constraints: {
      title: 'Constraint check',
      advisory: 'Advisory: the overlaps below inform the officers; the decision stays theirs.',
      none: 'No constraint check has run on this submission yet.',
      run: 'Run the constraint check',
      rerun: 'Run the check again',
      requested: 'The check was requested. Its report appears here when it is ready.',
      queued: 'The constraint check is waiting to run…',
      running: 'The constraint check is running…',
      failed: 'The constraint check could not finish: {{reason}}',
      newer: {
        queued:
          'A newer check of revision {{revision}} is waiting to run. Its report replaces this one when it is ready.',
        running: 'A newer check of revision {{revision}} is running. Its report replaces this one when it is ready.',
        failed: 'A newer check of revision {{revision}} could not finish: {{reason}} The report below still counts.',
      },
      meta: 'Revision {{revision}}, checked {{date}}',
      packVersion: 'Constraint kinds of pack version {{version}}',
      older: 'This report is of revision {{revision}}; the current revision is {{current}}.',
      trigger: {
        staged: 'Run after staging',
        on_demand: 'Run by an officer',
      },
      noFindings: 'No constraint layer was checked.',
      layer: 'Layer: {{name}}',
      status: {
        overlap: 'Overlaps',
        clear: 'Clear',
        error: 'Could not check',
      },
      overlap: '{{area}} ({{pct}}% of the site)',
      features: 'Features overlapping: {{number}}',
      needsDetermination: 'Needs a determination',
      determinations: {
        title: 'Officer determinations',
        open: 'Determinations still needed before the decision: {{number}}',
        pending: 'Not yet determined',
        recorded: '{{outcome}} (determined {{date}})',
        note: 'Note: {{note}}',
        record: 'Record determination',
        change: 'Change determination',
      },
      determine: {
        title: 'Determination: {{kind}}',
        intro: 'Record what the officers decided about this constraint before the decision.',
        outcome: 'Outcome',
        note: 'Note',
        noteHint: 'Optional. Officers and the council departments see it; the applicant does not.',
        replaces: 'This replaces the determination recorded on {{date}}.',
        outcomeRequired: 'Choose an outcome.',
        confirm: 'Record',
        cancel: 'Cancel',
        done: 'The determination was recorded.',
      },
      outcomes: {
        not_affected: 'Not affected',
        acceptable: 'Acceptable',
        objection: 'Objection',
      },
      kinds: {
        hutan_simpan: 'Permanent forest reserve',
        ksas_tahap_1: 'Environmentally sensitive area, level 1',
        ksas_tahap_2: 'Environmentally sensitive area, level 2',
        rizab_sungai: 'River reserve',
        rizab_jalan: 'Road reserve',
        kawasan_banjir: 'Flood-prone area',
      },
      findingErrors: {
        layer_missing: 'The bound layer is no longer in the Library.',
        layer_unsupported: 'The bound layer is not a PostgreSQL or ArcGIS feature layer.',
        server_unavailable: "The layer's server could not be reached.",
        layer_srid_unknown: "The layer's coordinate system is not known.",
        query_failed: 'The layer could not be queried.',
        too_many_features: 'Too many features overlap the site to count them.',
        address_blocked: "The layer's address is not allowed.",
        other: 'The layer could not be checked.',
      },
      reportErrors: {
        site_missing: 'The register holds no site for this revision.',
        superseded: 'A newer report replaced it.',
        run_failed: 'The check stopped with an error.',
        other: 'The check stopped.',
      },
    },
    settings: {
      title: 'Constraint layers',
      description:
        'Bind each constraint kind of the pack to the Library layers that map it. Checks run after register staging and when an officer asks; their results are advisory.',
      kindsVersion: 'Constraint kinds of pack version {{version}}.',
      determination: 'Needs an officer determination',
      advisory: 'Advisory only',
      none: 'No layer bound.',
      add: 'Add',
      addLabel: 'Layer to bind to {{kind}}',
      choose: 'Choose a layer…',
      remove: 'Remove',
      removeLabel: 'Remove {{layer}} from {{kind}}',
      missingLayer: 'A layer no longer available ({{id}})',
      unlistedLayer: 'Layer {{id}}',
      loading: 'Loading the Library layers…',
      loadFailed: 'The Library layers could not be loaded.',
      capped: 'Only the first {{max}} layers of each type are listed.',
      noLayers: 'The Library has no PostgreSQL or ArcGIS feature layer to bind.',
      types: {
        postgresql: 'PostgreSQL layer',
        arcgis_feature: 'ArcGIS FeatureServer layer',
      },
      count: '{{used}} of {{max}} bindings used',
      tooMany: 'At most {{max}} bindings.',
      repeated: 'A layer is bound twice to the same kind.',
      unknownKind: 'Remove the bindings of kinds the pack no longer declares.',
      undeclared: 'Kinds the pack no longer declares',
    },
  },
  errors: {
    ...rulesStrings.errors,
    check_not_acceptable:
      'This drawing check cannot be accepted: it passed, was already accepted, or failed on its scale or on the council area.',
    register_row_not_found: 'That register row is not on the current revision of this submission.',
    class_row_changed: 'That register row changed since the page was loaded. Reload the page and try again.',
    class_row_not_staged: 'Only a staging register row of the current revision can be corrected.',
    class_path_invalid: 'That class is not one of the land-use classes of this submission.',
    class_unchanged: 'That row already has this class.',
    constraints_not_configured:
      'No constraint layers are bound yet. An administrator binds them in Settings → Planning.',
    register_rows_missing: 'The current revision has no register rows to check.',
    constraint_report_not_found: 'That constraint report is not on this case.',
    constraint_report_not_done: 'That constraint report has not finished yet. Reload the page and try again.',
    constraint_report_superseded: 'A newer constraint report replaced that one. Reload the page and try again.',
    constraint_kind_unknown: 'That constraint kind needs no determination in this report.',
    constraint_report_changed:
      'That constraint report changed since the page was loaded. Reload the page and try again.',
  },
  events: {
    plot_class_corrected: 'Register class corrected',
    constraints_checked: 'Constraint check recorded',
    rules_evaluated: 'Planning rules evaluated',
    // D-234: the accepting transition's own event, labelled as neutrally as the acceptance event it precedes.
    transition_check_accept_exceptions: 'Drawing check accepted',
  },
} as const
